import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  Button,
  Divider,
  CircularProgress,
  makeStyles,
  useTheme
} from "@material-ui/core";
import { APP_VERSION } from '../../config/version';
import { getSystemVersionInfo } from '../../services/system.api';
import { getVersionInfo } from '../../services/version.api';
import { useTimezone } from '../../hooks/useTimezone';
import { i18n } from '../../translate/i18n';
import renderMarkdownInline from '../../helpers/renderMarkdownInline';

// Meta por categoría (color + etiqueta) para los chips del resumen.
const CATEGORY_META = {
  added: { label: "Nuevas", color: "#16a34a" },
  changed: { label: "Cambios", color: "#2563eb" },
  fixed: { label: "Correcciones", color: "#f59e0b" },
  security: { label: "Seguridad", color: "#dc2626" },
  deprecated: { label: "Obsoletos", color: "#9333ea" },
  removed: { label: "Eliminados", color: "#6b7280" },
};
const CATEGORY_ORDER = ["added", "changed", "fixed", "security", "deprecated", "removed"];

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  sectionCard: {
    marginBottom: theme.spacing(2),
    borderRadius: 12,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.mode === 'dark' 
      ? '0 2px 8px rgba(0,0,0,0.3)' 
      : '0 2px 8px rgba(0,0,0,0.1)',
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: theme.mode === 'dark' 
        ? '0 4px 16px rgba(0,0,0,0.5)' 
        : '0 4px 16px rgba(0,0,0,0.15)',
      transform: 'translateY(-2px)',
    },
  },
  sectionHeader: {
    background: theme.mode === 'dark' 
      ? 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)' 
      : 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
    color: 'white',
    padding: theme.spacing(2),
    borderRadius: '12px 12px 0 0',
    marginBottom: 0,
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 600,
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  sectionContent: {
    padding: theme.spacing(2.5),
    backgroundColor: theme.palette.background.paper,
  },
  settingItem: {
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    borderRadius: 8,
    backgroundColor: theme.mode === 'dark' ? '#374151' : '#f8f9fa',
    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e9ecef'}`,
    transition: 'all 0.2s ease',
    height: '100%',
    '&:hover': {
      backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#e3f2fd',
      borderColor: theme.mode === 'dark' ? theme.palette.primary.light : '#1976d2',
    },
  },
  settingLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
    display: 'block',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 300,
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  versionBadge: {
    fontSize: '16px',
    fontWeight: 700,
    padding: '6px 16px',
  },
  infoBox: {
    padding: theme.spacing(2),
    borderRadius: 8,
    backgroundColor: theme.mode === 'dark' ? '#1e3a8a' : '#e3f2fd',
    border: `1px solid ${theme.mode === 'dark' ? '#3b82f6' : '#1976d2'}`,
    marginTop: theme.spacing(2),
  },
  highlightList: {
    margin: `${theme.spacing(1)}px 0 0 0`,
    paddingLeft: theme.spacing(2.5),
    "& li": {
      marginBottom: theme.spacing(0.75),
      lineHeight: 1.45,
    },
  },
  catChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  ctaButton: {
    marginTop: theme.spacing(1.5),
    textTransform: 'none',
    fontWeight: 700,
    backgroundColor: '#4f46e5',
    color: '#fff',
    '&:hover': { backgroundColor: '#4338ca' },
  },
  milestoneBanner: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2, 2.5),
    borderRadius: 12,
    color: '#fff',
    background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 55%, #2563eb 100%)',
    boxShadow: '0 8px 24px rgba(79,70,229,0.35)',
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'flex-start',
  },
  milestoneIcon: {
    fontSize: 34,
    lineHeight: 1,
  },
  milestoneTitle: {
    fontSize: '15px',
    fontWeight: 800,
    letterSpacing: 0.3,
    marginBottom: theme.spacing(0.5),
  },
  milestoneText: {
    fontSize: '13px',
    opacity: 0.95,
    lineHeight: 1.5,
  },
}));

// Estilo del chip "tipo de cambio" según SemVer (dato, no hardcode).
const CHANGE_TYPE_STYLE = {
  MAJOR: { bg: '#7c3aed', label: 'MAJOR' },
  MINOR: { bg: '#2563eb', label: 'MINOR' },
  PATCH: { bg: '#6b7280', label: 'PATCH' },
};

function formatReleaseLabel(dateStr, formatDate, formatDateTime, naLabel = "N/A") {
  if (!dateStr || dateStr === "N/A") return naLabel;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    // Una fecha SIN hora (la del CHANGELOG) no se convierte de zona: «2026-10-05 00:00»
    // tratada como UTC caía en el día anterior en América (salía 04/10 en vez de 05/10).
    // Mediodía local: el día es el mismo en cualquier zona horaria.
    try {
      return new Date(`${dateStr}T12:00:00`).toLocaleDateString();
    } catch {
      return dateStr;
    }
  }
  try {
    return formatDateTime ? formatDateTime(dateStr) : String(dateStr);
  } catch {
    return String(dateStr);
  }
}

export default function VersionInfo() {
  const classes = useStyles();
  const theme = useTheme();
  const { formatDateTime, formatDate } = useTimezone();
  const [pkgVersion, setPkgVersion] = useState(null);
  const [semantic, setSemantic] = useState(null);
  const [loadingVersion, setLoadingVersion] = useState(true);

  useEffect(() => {
    const loadVersionInfo = async () => {
      setLoadingVersion(true);
      try {
        const [sysRes, pkgRes] = await Promise.all([
          getSystemVersionInfo().catch(() => ({ data: null })),
          getVersionInfo().catch(() => ({ data: null })),
        ]);
        if (sysRes?.data?.semanticVersion) {
          setSemantic(sysRes.data.semanticVersion);
        }
        if (pkgRes?.data) {
          setPkgVersion(pkgRes.data);
        }
      } catch (error) {
        console.error("Error cargando información de versión:", error);
      } finally {
        setLoadingVersion(false);
      }
    };
    loadVersionInfo();
  }, []);

  const displayVersion =
    semantic?.currentVersion || pkgVersion?.version || APP_VERSION;
  const changeType = (semantic?.changeType || "MINOR").toUpperCase();
  const changeSummary = semantic?.changeSummary || null;
  const ctStyle = CHANGE_TYPE_STYLE[changeType] || CHANGE_TYPE_STYLE.MINOR;
  const lastUpdateLabel = formatReleaseLabel(
    semantic?.lastVersionUpdate || semantic?.lastReleaseDate,
    formatDate,
    formatDateTime,
    i18n.t("settings.versionPage.na")
  );
  const latest = semantic?.latestRelease;

  if (loadingVersion) {
    return (
      <Box className={classes.container}>
        <Card className={classes.sectionCard}>
          <CardContent className={classes.sectionContent}>
            <Box className={classes.loadingContainer}>
              <CircularProgress size={40} />
              <Typography variant="body2" style={{ color: '#6B7280' }}>
                {i18n.t("settings.versionPage.loading")}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      {/* ✅ INFORMACIÓN PRINCIPAL DE VERSIÓN */}
      <Card className={classes.sectionCard}>
        <Box className={classes.sectionHeader}>
          <Typography className={classes.sectionTitle}>
            <span role="img" aria-label={i18n.t("settings.versionPage.ariaVersion")}>📦</span> {i18n.t("settings.versionPage.title")}
          </Typography>
        </Box>
        <CardContent className={classes.sectionContent}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box className={classes.settingItem}>
                <Typography className={classes.settingLabel}>
                  {i18n.t("settings.versionPage.currentVersion")}
                </Typography>
                <Box display="flex" alignItems="center" gap={1} mt={1} flexWrap="wrap">
                  <Chip
                    label={`v${displayVersion}`}
                    className={classes.versionBadge}
                    style={{
                      backgroundColor: '#3b82f6',
                      color: 'white',
                    }}
                  />
                  {latest?.version && latest.version !== displayVersion && (
                    <Chip
                      label={i18n.t("settings.versionPage.changelogChip", { version: latest.version })}
                      size="small"
                      variant="outlined"
                      style={{ fontSize: '11px' }}
                    />
                  )}
                </Box>
                {latest?.date && (
                  <Typography variant="caption" style={{ display: 'block', marginTop: 8, color: '#6B7280' }}>
                    {i18n.t("settings.versionPage.releaseDateChangelog")}{" "}
                    {formatReleaseLabel(latest.date, formatDate, formatDateTime, i18n.t("settings.versionPage.na"))}
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box className={classes.settingItem}>
                <Typography className={classes.settingLabel}>
                  {i18n.t("settings.versionPage.changeType")}
                </Typography>
                <Box display="flex" alignItems="center" gap={1} mt={1} flexWrap="wrap">
                  <Chip
                    label={ctStyle.label}
                    size="small"
                    style={{
                      backgroundColor: ctStyle.bg,
                      color: "white",
                      fontSize: "11px",
                      fontWeight: 700,
                      letterSpacing: 0.4,
                    }}
                  />
                  {semantic?.lastUpdateSource === "git-last-commit" && (
                    <Chip
                      label={i18n.t("settings.versionPage.gitDateChip")}
                      size="small"
                      style={{ fontSize: "10px" }}
                    />
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>

          {changeType === "MAJOR" && (
            <Box className={classes.milestoneBanner}>
              <span className={classes.milestoneIcon} role="img" aria-label="hito">🏛️</span>
              <Box>
                <Typography className={classes.milestoneTitle}>
                  {i18n.t("settings.versionPage.milestoneTitle")}
                </Typography>
                <Typography className={classes.milestoneText}>
                  {changeSummary || i18n.t("settings.versionPage.milestoneDefault")}
                </Typography>
              </Box>
            </Box>
          )}

          {latest && (
            <Box className={classes.infoBox}>
              <Typography
                variant="subtitle2"
                style={{ fontWeight: 700, marginBottom: 12, color: theme.palette.text.primary }}
              >
                {i18n.t("settings.versionPage.releaseSummary")}
                {latest.version ? ` (v${latest.version})` : ""}
                {typeof latest.totalChanges === "number" && latest.totalChanges > 0
                  ? ` · ${i18n.t("settings.versionPage.changesCount", { count: latest.totalChanges })}`
                  : ""}
              </Typography>

              {/* Vista escaneable: chips por categoría con conteo */}
              {latest.categoryCounts && (
                <Box className={classes.catChips}>
                  {CATEGORY_ORDER.map((cat) => {
                    const n = latest.categoryCounts[cat] || 0;
                    if (!n) return null;
                    const m = CATEGORY_META[cat];
                    return (
                      <Chip
                        key={cat}
                        size="small"
                        label={`${m.label}: ${n}`}
                        style={{
                          backgroundColor: `${m.color}22`,
                          color: m.color,
                          fontWeight: 700,
                          border: `1px solid ${m.color}55`,
                        }}
                      />
                    );
                  })}
                </Box>
              )}

              {/* Destacados (markdown renderizado, sin marcadores crudos) */}
              {latest.highlights?.length > 0 && (
                <ul className={classes.highlightList}>
                  {latest.highlights.slice(0, 6).map((line, idx) => (
                    <li key={idx}>
                      <Typography variant="body2" style={{ color: theme.palette.text.secondary }}>
                        {renderMarkdownInline(line)}
                      </Typography>
                    </li>
                  ))}
                </ul>
              )}

              <Button
                variant="contained"
                size="small"
                disableElevation
                href="/releases"
                className={classes.ctaButton}
              >
                {i18n.t("settings.versionPage.viewFullHistory")}
              </Button>
            </Box>
          )}

          <Divider style={{ margin: '20px 0' }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Box className={classes.settingItem}>
                <Typography className={classes.settingLabel}>
                  {i18n.t("settings.versionPage.frontendVersion")}
                </Typography>
                <Typography variant="body2" style={{ marginTop: 8, color: '#6B7280', fontWeight: 500 }}>
                  v{APP_VERSION}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box className={classes.settingItem}>
                <Typography className={classes.settingLabel}>
                  {i18n.t("settings.versionPage.apiVersion")}
                </Typography>
                <Typography variant="body2" style={{ marginTop: 8, color: '#6B7280', fontWeight: 500 }}>
                  {pkgVersion?.version ? `v${pkgVersion.version}` : i18n.t("settings.versionPage.na")}
                  {pkgVersion?.versionSystem ? (
                    <Typography component="span" variant="caption" style={{ display: 'block', color: '#9CA3AF' }}>
                      {i18n.t("settings.versionPage.baseWhaticket")} {pkgVersion.versionSystem}
                    </Typography>
                  ) : null}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box className={classes.settingItem}>
                <Typography className={classes.settingLabel}>
                  {i18n.t("settings.versionPage.lastUpdate")}
                </Typography>
                <Typography variant="body2" style={{ marginTop: 8, color: '#6B7280', fontWeight: 500 }}>
                  {lastUpdateLabel}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Divider style={{ margin: '20px 0' }} />
          
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Typography variant="caption" style={{ color: '#6B7280' }}>
              {i18n.t("settings.versionPage.semanticFooter")}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => window.location.href = '/releases'}
              style={{
                textTransform: 'none',
                borderRadius: 8,
                fontSize: '12px',
                padding: '6px 16px'
              }}
            >
              {i18n.t("settings.versionPage.fullHistory")}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

